// The pieces of a repertoire's page that only draw what they are given: the
// tabs' lists, a habit's two moves and its note, the tree of lines, and the
// red dot for games that left the line. The page (Repertoire.tsx) owns what
// is shown; the logic of it is repertoire-view.ts.

import { Fragment, useState, type ReactNode } from "react";
import { fmtScore, moveNumberOf } from "../app/format.ts";
import { byMain, sideToMove, type Block, type Habit, type TreeMove, type TreeNode } from "./repertoire-view.ts";

/** What a flag on a move of yours means. */
export const FLAG_TEXT: Record<string, string> = {
  unsound: "The book's evaluation says this loses. Check the source.",
  dubious: "The book's evaluation prefers another move here.",
};

/** The red dot for games that left the line: bigger for more of them. */
export function MissDot({ n, title }: { n: number; title: string }) {
  if (!n) return null;
  const size = n >= 5 ? 4 : n >= 3 ? 3 : n;
  return <span className={`miss-dot miss-${size}`} title={title} aria-label={title} />;
}

export const gamesText = (n: number) => `${n} game${n === 1 ? "" : "s"}`;

export function List({ title, sub, items, action }: { title: string; sub?: string; items: ReactNode[]; action?: ReactNode }) {
  const [all, setAll] = useState(false);
  if (!items.length) return null;
  const HEAD = 8;
  return (
    <section className="card rep-list">
      <div className="rep-list-head">
        <h2>{title}</h2>
        {action || null}
      </div>
      {sub ? <p className="help">{sub}</p> : null}
      {all ? items : items.slice(0, HEAD)}
      {items.length > HEAD && !all ? (
        <button type="button" className="link-btn rep-more" onClick={() => setAll(true)}>
          Show {items.length - HEAD} more
        </button>
      ) : null}
    </section>
  );
}

/** A habit, shown: the move that keeps getting played marked red and the
 *  line's move green, each a button — your move to see what the book makes
 *  of it, the line's to walk on down the line. */
export function HabitWhere({
  hb,
  nodes,
  chip,
  reveal,
  onOff,
}: {
  hb: Habit;
  nodes: Record<string, TreeNode>;
  chip: (key: string, label: string, cls: string, onClick: () => void, title?: string) => ReactNode;
  reveal: (epd: string) => void;
  onOff: () => void;
}) {
  const node = nodes[hb.epd];
  return (
    <div className="rep-where">
      <span className="mark-key bad" />
      {hb.played_fen ? chip(`habit:${hb.played_uci}`, hb.played_san, "bad", onOff, "What the book says about your move") : <span>{hb.played_san}</span>}
      <span className="faint">you</span>
      <span className="mark-key good" />
      {(node?.moves || []).map((m) => chip(`good:${m.uci}`, m.san + (m.nag || ""), "yours good", () => reveal(m.to), m.comment || "Play on down the line"))}
      <span className="faint">your line</span>
    </div>
  );
}

export function HabitNote({ hb, nodes }: { hb: Habit; nodes: Record<string, TreeNode> }) {
  const node = nodes[hb.epd];
  const main = (node?.moves || []).find((m) => m.main) || node?.moves?.[0];
  const games = (hb.games || []).length;
  const lost = Math.round(hb.avg_lost * 50);
  return (
    <p className="rep-note">
      You play {hb.played_san} here, in {games} {games === 1 ? "game" : "games"}.{" "}
      {hb.avg_before != null && hb.avg_after != null && lost >= 1
        ? `On average it takes the position from ${fmtScore({ kind: "cp", value: hb.avg_before })} to ${fmtScore({ kind: "cp", value: hb.avg_after })}, about ${lost} points of winning chances. `
        : lost >= 1
          ? `It costs you about ${lost} points of winning chances each time. `
          : "It costs you nothing, by the engine. "}
      {main?.comment || ""}
    </p>
  );
}

/** The tree of lines: rows along the main moves, folds for the others. */
export function Blocks({
  blocks,
  open,
  toggle,
  at,
  color,
  cards,
  missedAt,
  onSelect,
  plus,
}: {
  blocks: Block[];
  open: (at: string) => boolean;
  toggle: (at: string) => void;
  at: { nodeEpd: string; mv: TreeMove | null };
  color: string;
  cards: Record<string, { step: number }>;
  missedAt: (epd: string) => number;
  onSelect: (nodeEpd: string, mv: TreeMove) => void;
  plus: (epd: string) => ReactNode;
}) {
  return (
    <>
      {blocks.map((block, i) =>
        block.t === "fold" ? (
          <div key={i} className="variations" hidden={!open(block.at)}>
            {block.variations.map((rows, j) => (
              <div key={j} className="variation">
                <Blocks blocks={rows} open={open} toggle={toggle} at={at} color={color} cards={cards} missedAt={missedAt} onSelect={onSelect} plus={plus} />
              </div>
            ))}
          </div>
        ) : (
          <div key={i} className="tree-row">
            {block.items.map((item, j) => {
              switch (item.t) {
                case "start":
                  return (
                    <span key={j} className="faint">
                      Start
                    </span>
                  );
                case "name":
                  return (
                    <span key={j} className="node-name">
                      {item.name}
                    </span>
                  );
                case "transposes":
                  return (
                    <span key={j} className="faint transposes">
                      (transposes)
                    </span>
                  );
                case "plus":
                  return <Fragment key={j}>{plus(item.epd)}</Fragment>;
                case "toggle": {
                  const isOpen = open(item.at);
                  return (
                    <button
                      key={j}
                      type="button"
                      className={"more-lines" + (isOpen ? " open" : "")}
                      title={
                        item.yoursNext
                          ? `${item.count} more of your moves accepted from here`
                          : `${item.count} more ${item.count === 1 ? "reply" : "replies"} prepared from here`
                      }
                      onClick={() => toggle(item.at)}
                    >
                      <span className="caret">{isOpen ? "▾" : "▸"}</span>
                      {item.count}
                    </button>
                  );
                }
                case "move": {
                  const { node, nodeEpd, mv } = item;
                  const yours = sideToMove(node.fen) === color;
                  const card = yours ? cards[nodeEpd] : null;
                  const white = node.ply % 2 === 0;
                  const cls = ["mv", yours ? "yours" : "theirs", mv.flag ? "flag-" + mv.flag : "", mv.trap ? "trap" : "", card && card.step >= 3 ? "learned" : "", at.mv && at.nodeEpd === nodeEpd && at.mv.uci === mv.uci ? "at" : ""]
                    .filter(Boolean)
                    .join(" ");
                  return (
                    <button
                      key={j}
                      type="button"
                      className={cls}
                      title={mv.comment || (mv.flag ? FLAG_TEXT[mv.flag] : mv.trap ? "A trap: this loses for them" : undefined)}
                      onClick={() => onSelect(nodeEpd, mv)}
                    >
                      {item.number ? <span className="mv-num">{white ? `${moveNumberOf(node.ply)}.` : `${moveNumberOf(node.ply)}…`}</span> : null}
                      {mv.san}
                      {mv.nag ? <span className="nag">{mv.nag}</span> : null}
                      {yours && byMain(node.moves)[0]?.uci === mv.uci ? (
                        <MissDot n={missedAt(nodeEpd)} title={`You left the line here in ${gamesText(missedAt(nodeEpd))} in the last month`} />
                      ) : null}
                    </button>
                  );
                }
              }
            })}
          </div>
        )
      )}
    </>
  );
}
